import { DatePipe, formatDate } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SnackbarComponent } from 'app/snackbar/snackbar.component';
import { ApiService } from 'app/ts/ApiService';
import { Report } from 'app/ts/models/broker-classes';
import { PreVetRecords, ProcessedRecords, ApprovalRecords, columnHeaders } from 'app/ts/models/broker-interfaces';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-broker-reports',
  templateUrl: './broker-reports.component.html',
  styleUrls: ['./broker-reports.component.scss', '../../forms.shared.scss']
})
export class BrokerReportsComponent implements OnInit {
  
  reports: string[] = [
    'Approval Report',
    'Process Report'
  ]

  @ViewChild(MatPaginator) paginator: MatPaginator;
  displayedColumns: string[];
  columnsToDisplay: string[];
  dataSource = new MatTableDataSource([]);
  idNumber: string;
  prevets: PreVetRecords[];
  process: ProcessedRecords[];
  approved: ApprovalRecords[];
  reportRequest = new Report();
  staffId: number;
  columns: columnHeaders[];
  reportName: string = '';
  b64Data: string;
  length = 50;
  pageSize = 5;
  pageIndex = 0;
  pageSizeOptions: number[] = [5, 10, 25, 50, 100];
  isAdmin: boolean;
  showResults: boolean;
  filterValue: string;

  isLoading: boolean = false;

  pageEvent: PageEvent;
  predefinedDates: boolean = false;
  startDate: Date = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);
  endDate: Date = new Date();
  minDatePicker: Date = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000);
  maxDatePicker: Date = new Date(Date.now());

  allowedPrevetReportId: number[] = [0, 11, 490, 639, 729, 737] //Awie, Johan and Tiaan's staffIds for prevet report access

  handlePageEvent(event: PageEvent) {
    this.length = event.length;
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
  }

  setPageSizeOptions(setPageSizeOptionsInput: string) {
    if (setPageSizeOptionsInput) {
      this.pageSizeOptions = setPageSizeOptionsInput.split(',').map(str => +str);
    }
  }
  constructor(private api: ApiService, private snackbar: MatSnackBar) {
  }

  reportDetails: FormGroup;

  selectChangeHandler(event: { value: string; }): void {
    this.predefinedDates = false;
    this.reportName = event.value;
    this.dataSource = new MatTableDataSource([]);
  }

  applyFilter(event: Event) {
    this.filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = this.filterValue?.trim().toLowerCase();
  }

  ngOnInit() {
    this.isAdmin = JSON.parse(sessionStorage.getItem("isSuper"));
    if (this.isAdmin && this.allowedPrevetReportId.includes(Number(sessionStorage.getItem('staffId')))) {
      this.reports.push('Prevet Report');
    }
    this.staffId = Number(sessionStorage.getItem('staffId'));

    this.showResults = true;
  }

  @ViewChild('TABLE', { static: false }) TABLE: ElementRef;
  @ViewChild(MatSort) sort: MatSort;

  title = 'Excel';
  onDownloadClick() {
    var headersArr = [];
    var headers = [];
  
    if (this.dataSource.filteredData.length > 0) {
      // Extract keys from the first object in dataSource.filteredData
      headers = Object.keys(this.dataSource.filteredData[0]);
    }
  
    headersArr.push(headers);
  
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
    XLSX.utils.sheet_add_aoa(ws, headersArr);
    this.dataSource.filter = this.filterValue?.trim().toLowerCase();
    XLSX.utils.sheet_add_json(ws, this.dataSource.filteredData, { origin: 'A2', skipHeader: true });
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${this.reportName}-${formatDate(new Date(), 'yyyy/MM/dd', 'en')}.xlsx`);
  }
  
  getResults(): void {
    this.showResults = false;
  }
  
  getReport(): void {
    this.isLoading = true;
    this.reportRequest.staffId = this.staffId;
    this.reportRequest.idNumber = this.idNumber ? this.idNumber : '';
    this.reportRequest.source = 'payroll';
    this.getResults();
    switch (this.reportName) {
      case "Prevet Report":

        this.displayedColumns = [
          'brokerName',
          'channel',
          'dateCreated',
          'prevetCheck',
          'prevetDate',
          'idNumber',
          'contactNumber',
          'hasDefault',
          'hasJudgment',
          'isDeceased',
          'underDebtReview',
          'hasPPAccounts',
        ];

        this.columns = [
          { DisplayName: 'Broker', DbName: 'brokerName' },
          { DisplayName: 'Channel', DbName: 'channel' },
          { DisplayName: 'Date Created', DbName: 'dateCreated' },
          { DisplayName: 'Prevet Check', DbName: 'prevetCheck' },
          { DisplayName: 'Prevet Created', DbName: 'prevetDate' },
          { DisplayName: 'Id Number', DbName: 'idNumber' },
          { DisplayName: 'Contact Number', DbName: 'contactNumber' },
          { DisplayName: 'Has Default', DbName: 'hasDefault' },
          { DisplayName: 'Has Judgment ', DbName: 'hasJudgment' },
          { DisplayName: 'Is Deceased', DbName: 'isDeceased' },
          { DisplayName: 'Under Debt Review', DbName: 'underDebtReview' },
          { DisplayName: 'Has PPAdverse', DbName: 'hasPPAccounts' },
        ];

        this.reportRequest.startDate = new Date(this.startDate).toISOString().split('T')[0];
        this.reportRequest.endDate = new Date(this.endDate).toISOString().split('T')[0];
        
        if (this.reportRequest.endDate < this.reportRequest.startDate) {
          this.openSnackBar('End date cannot be before start date.', 'error-snackbar');
          this.isLoading = false;
          return;
        }
        //Default date selection to 2 months for reporting purposes
        if (!this.predefinedDates) {
          this.reportRequest.startDate = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];;
          this.reportRequest.endDate = new Date(Date.now()).toISOString().split('T')[0];
        }

        this.columnsToDisplay = this.displayedColumns.slice();

        this.api.generatePrevetReport(this.reportRequest).subscribe((data: PreVetRecords[]) => {
          this.dataSource.data = data;
          this.isLoading = false;
        });

        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        break;
      case "Process Report":
        this.reportRequest.idNumber = this.idNumber ? this.idNumber : '';
        this.reportRequest.staffId = this.staffId ? this.staffId : 0;

        this.displayedColumns = [
          'broker_name',
          'channel',
          'client_name',
          'zaid',
          'mobile',
          'note',
          'note_date',
          'submittedDate',
          'switched',
          'updatedBy',
          'updatedDate'
          // 'loan_id',
          // 'loan_status'
        ];

        this.columns = [
          { DisplayName: 'Broker', DbName: 'broker_name' },
          { DisplayName: 'Channel', DbName: 'channel' },
          { DisplayName: 'Client', DbName: 'client_name' },
          { DisplayName: 'Id Number', DbName: 'zaid' },
          { DisplayName: 'Mobile', DbName: 'mobile' },
          { DisplayName: 'Note', DbName: 'note' },
          { DisplayName: 'Note Date', DbName: 'note_date' },
          { DisplayName: 'Submitted On', DbName: 'submittedDate'},
          { DisplayName: 'Switched', DbName: 'switched' },
          { DisplayName: 'Updated By', DbName: 'updatedBy' },
          { DisplayName: 'Updated Date', DbName: 'updatedDate' }
          // { DisplayName: 'Loan Id', DbName: 'loan_id' },
          // { DisplayName: 'Loan Status', DbName: 'loan_status'}
        ]

        this.columnsToDisplay = this.displayedColumns.slice();
        if (this.isAdmin) {
          this.reportRequest.staffId = 0;
        }

        this.api.generateProcessReport(this.reportRequest).subscribe((data: ProcessedRecords[]) => {
          this.dataSource.data = data;
          this.isLoading = false;
        })

        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        break;

      case "Approval Report":
        this.displayedColumns = [
          'accountNr',
          'approved_principal',
          'productName',
          'loanTerm',
          'approvedDate',
          'brokerName',
          'clientName',
          'clientSurname',
          'external_id',
        ];

        this.columns = [
          { DisplayName: 'Account No', DbName: 'accountNr' },
          { DisplayName: 'Principal', DbName: 'approved_principal' },
          { DisplayName: 'Product', DbName: 'productName' },
          { DisplayName: 'Loan Term', DbName: 'loanTerm' },
          { DisplayName: 'Approved On', DbName: 'approvedDate' },
          { DisplayName: 'Broker', DbName: 'brokerName' },
          { DisplayName: 'Client Name', DbName: 'clientName' },
          { DisplayName: 'Client Surname', DbName: 'clientSurname' },
          { DisplayName: 'Client Id No', DbName: 'external_id' },
        ]

        this.columnsToDisplay = this.displayedColumns.slice();

        if (this.isAdmin) {
          this.reportRequest.staffId = 0;
        }

        this.api.generateApprovalReport(this.reportRequest).subscribe((data: ApprovalRecords[]) => {
          this.dataSource.data = data;
          this.isLoading = false;
        })

        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        break;
      case "Letter of Decline":
        this.reportRequest.idNumber = this.idNumber;
        this.api.getDeclinedLetter(this.reportRequest).subscribe((data: any) => {
          downloadPdf(data, `Decline Letter_client-${this.idNumber}`);
        });
        break;
    }

    function downloadPdf(b64: any, fileName: any) {
      const src = `data:application/pdf;base64, ${b64}`;
      const link = document.createElement('a');
      link.href = src;
      link.download = `${fileName}.pdf`;
      link.click();
    }
  }

  
  maskRSAID(idNumber: string): string {
    let masked = '';

    if (!this.isAdmin) {
      masked = idNumber.replace(/.(?=.{4})/g, '*');
    }
    else {
      masked = idNumber;
    }

    return masked;
  }

  openSnackBar(msg: string, panel: string) {
    this.snackbar.openFromComponent(SnackbarComponent, {
      data: msg,
      panelClass: [panel],
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }
}
